import { useCallback, useRef, useState } from "react";
import { MCP_AGENT_CHAT_TIMEOUT_MS } from "./constants";
import { messagesToAgentChatRequest, playgroundUiMessage, type McpPlaygroundMessage } from "./chatMessages";

const CHAT_REQUEST_FAILED_MESSAGE = "Chat request failed. Please try again.";

type UseMcpPlaygroundAgentChatOptions = {
  api: string;
  fetch: typeof fetch;
};

export function useMcpPlaygroundAgentChat({ api, fetch: authedFetch }: UseMcpPlaygroundAgentChatOptions) {
  const [messages, setMessagesState] = useState<McpPlaygroundMessage[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | undefined>();
  const isLoadingRef = useRef(false);
  const messagesRef = useRef(messages);
  messagesRef.current = messages;

  const setMessages = useCallback(
    (updater: McpPlaygroundMessage[] | ((current: McpPlaygroundMessage[]) => McpPlaygroundMessage[])) => {
      setMessagesState((current) => (typeof updater === "function" ? updater(current) : updater));
    },
    []
  );

  const sendMessage = useCallback(
    async (text: string): Promise<boolean> => {
      const trimmed = text.trim();
      if (!trimmed || isLoadingRef.current) {
        return false;
      }

      const userMessage = playgroundUiMessage("user", trimmed);
      const requestMessages = [...messagesRef.current, userMessage];

      setMessagesState(requestMessages);
      setIsLoading(true);
      isLoadingRef.current = true;
      setError(undefined);

      let timeoutId: number | undefined;
      try {
        const controller = new AbortController();
        timeoutId = window.setTimeout(() => controller.abort(), MCP_AGENT_CHAT_TIMEOUT_MS);

        const response = await authedFetch(api, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Accept: "text/plain"
          },
          body: JSON.stringify({ messages: messagesToAgentChatRequest(requestMessages) }),
          signal: controller.signal
        });

        if (!response.ok) {
          let errorMessage = CHAT_REQUEST_FAILED_MESSAGE;
          const contentType = response.headers.get("content-type") ?? "";
          if (contentType.includes("application/json")) {
            const body = (await response.json()) as { error?: string };
            if (body.error) {
              errorMessage = body.error;
            }
          } else {
            const responseText = await response.text();
            if (responseText) {
              errorMessage = responseText;
            }
          }
          throw new Error(errorMessage);
        }

        const assistantText = await response.text();
        setMessagesState((current) => [...current, playgroundUiMessage("assistant", assistantText)]);
        return true;
      } catch (caught) {
        const timedOut = caught instanceof Error && caught.name === "AbortError";
        const timeoutHint = api.includes("/local-backoffice")
          ? "Chat request timed out. Confirm bin/rails server is running at localhost:3000."
          : "Chat request timed out. Please try again.";
        const nextError = timedOut
          ? new Error(timeoutHint)
          : caught instanceof Error
            ? caught
            : new Error(CHAT_REQUEST_FAILED_MESSAGE);
        setError(nextError);
        return true;
      } finally {
        if (timeoutId !== undefined) {
          window.clearTimeout(timeoutId);
        }
        setIsLoading(false);
        isLoadingRef.current = false;
      }
    },
    [api, authedFetch]
  );

  return {
    messages,
    sendMessage,
    setMessages,
    isLoading,
    error
  };
}
